import { Injectable } from '@angular/core';
import { AppConfig } from 'src/app/core/configs/app.config';
import { CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { from, Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmExitModalComponent } from '../confirm-exit-modal/confirm-exit-modal.component';
import { CanComponentDeactivate } from './can-deactivate.interface';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class CanDeactivateGuard implements CanDeactivate<CanComponentDeactivate> {
    constructor(private dialog: MatDialog) {}

    canDeactivate(
        component: CanComponentDeactivate,
        route: ActivatedRouteSnapshot,
        currentState: RouterStateSnapshot,
        nextState?: RouterStateSnapshot
    ): Observable<boolean> | boolean {
        const campaignId = route.params['id'];
        const targetUrl = nextState?.url;
        const currentUrl = currentState.url;
        
        // Only show modal when leaving step-4; all other steps allow navigation freely
        const isOnStep4 = currentUrl.includes('step-4');
        if (!isOnStep4) {
            if (targetUrl?.startsWith('/dashboard')) {
                return true;
            }
            return true; // All other steps allow navigation freely
        }

        // From step-4: allow navigation to checkout/payment without modal
        const isMovingForward = 
            targetUrl?.includes('complete-payment') || 
            targetUrl?.includes('checkout') || 
            targetUrl?.includes('step-5');

        if (isMovingForward) {
            return true;
        }

        // Moving around inside step 4 (e.g. its medias-overview child) is not leaving it.
        if (targetUrl?.includes('step-4')) {
            return true;
        }

        // Going back to an earlier step reads differently from leaving the booking flow
        // altogether (a sidebar destination), so the dialog is told which it is.
        //
        // It has to be an earlier step *of this campaign*: the sidebar's "New Campaign" points
        // at /campaign/new, which redirects to step-1/, so matching on the step alone treated
        // starting a new campaign as going back.
        const isGoingBackToStep =
            !!campaignId &&
            !!targetUrl &&
            targetUrl.includes(campaignId) &&
            (targetUrl.includes('step-1') ||
                targetUrl.includes('step-2') ||
                targetUrl.includes('step-3'));

        // Every other way out of step 4 — an earlier step, the dashboard, any sidebar
        // destination — offers the same discard / save / keep-editing choice.
        const mustAsk =
            !!component.shouldConfirmBeforeLeaving &&
            component.shouldConfirmBeforeLeaving();

        // canDeactivate() saves the draft itself when every visual is complete, so the check
        // above has to come first: otherwise the save fires silently on the way out and the
        // client never gets asked.
        if (!mustAsk) {
            const canDeactivateResult: any = component.canDeactivate();

            if (canDeactivateResult instanceof Promise) {
                return from(canDeactivateResult);
            }
            if (canDeactivateResult === true) {
                return true;
            }
        }

        const message = component.getExitMessage ? component.getExitMessage() : undefined;

        const dialogRef = this.dialog.open(ConfirmExitModalComponent, {
            width: '500px',
            maxHeight: AppConfig.dialog_max_height,
            data: {
                campaignId,
                component,
                message,
                offerSaveChoice: true,
                saveChoiceMode: isGoingBackToStep ? 'back' : 'exit'
            },
            disableClose: true,
            panelClass: 'exit-modal-dialog',
            hasBackdrop: true
        });

        return dialogRef.afterClosed().pipe(map(result => result === true));
    }
}
